<template>
    <GenericModal
        :modelValue="props.modelValue"
        @update:modelValue="handleModalUpdate"
        :closable="true"
        :closeOnClickOutside="true"
        :closeOnEsc="true"
        :contentPadding="'0'"
        :fullscreenMobile="true"
        :darkMode="true"
    >
        <div class="purchase-stars-modal">
            <div class="modal-header">
                <h2 class="title">{{ $t('buy_stars.title') }}</h2>
                <p class="subtitle-text">
                    {{ $t('buy_stars.default_subtitle') }}
                </p>
            </div>

            <div v-if="loading" class="loading-message">
                {{ $t('general.loading') }}
            </div>

            <div v-else-if="error" class="error-message">
                {{ $t('general.general-error') }}
            </div>

            <div v-else class="purchase-options-container">
                <RadioOption
                    v-for="(option, index) in purchaseOptionsWithDiscount"
                    :key="option.id || index"
                    :isSelected="selectedOption?.id === option.id"
                    @click="selectOption(option)"
                >
                    <div class="option-left">
                        <div class="stars-container">
                            <i class="pi pi-star-fill star-icon star-first"></i>
                            <i
                                v-if="index > 0"
                                class="pi pi-star-fill star-icon star-second"
                            ></i>
                        </div>
                        <span class="stars-amount">{{ option.amount }}</span>
                    </div>

                    <div class="option-right">
                        <span class="price"
                            >${{ formatDecimal128(option.price) }}</span
                        >
                        <span
                            class="discount-badge"
                            v-if="option.calculatedDiscount > 0"
                        >
                            {{
                                $t('buy_stars.discount_save', {
                                    discount: option.calculatedDiscount,
                                })
                            }}
                        </span>
                    </div>
                </RadioOption>
            </div>

            <PrimaryButton
                class="continue-button"
                :disabled="!selectedOption || loading || processingPayment"
                @click="handleCheckout"
                :label="$t('buy_stars.checkout_button')"
                :loading="processingPayment"
                :labelFontSize="'16px'"
                :labelFontWeight="'600'"
            />
        </div>
    </GenericModal>
</template>

<script setup>
import GenericModal from '@/components/shared/core/GenericModal.vue'
import RadioOption from '@/components/shared/core/RadioOption.vue'
import PrimaryButton from '@/components/shared/buttons/primary-button.vue'
import { ref, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import transactionService from '@/services/api/transaction.service'
import { usePaymentStore } from '@/stores/payment.store'
import { usePackStore } from '@/stores/pack.store'
import { useAuthStore } from '@/stores/auth.store'
import { useModalStore } from '@/stores/modal.store'
import { storeToRefs } from 'pinia'
import { formatDecimal128 } from '@/utils'

const { t } = useI18n()
const paymentStore = usePaymentStore()
const packStore = usePackStore()
const authStore = useAuthStore()
const modalStore = useModalStore()

const { purchaseOptions, loading, error } = storeToRefs(packStore)

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:modelValue'])
const selectedOption = ref(null)
const processingPayment = ref(false)

// Calculate discount percentages based on the smallest pack
const purchaseOptionsWithDiscount = computed(() => {
    if (!purchaseOptions.value || purchaseOptions.value.length === 0) {
        return []
    }

    // Find the smallest pack (reference for calculating discounts)
    const sortedOptions = [...purchaseOptions.value].sort(
        (a, b) => a.amount - b.amount
    )
    const smallestPack = sortedOptions[0]

    // Calculate price per star for the smallest pack
    const smallestPricePerStar =
        parseFloat(formatDecimal128(smallestPack.price)) / smallestPack.amount

    // Calculate discount for each option
    return purchaseOptions.value.map((option) => {
        const currentPricePerStar =
            parseFloat(formatDecimal128(option.price)) / option.amount
        const savings =
            ((smallestPricePerStar - currentPricePerStar) /
                smallestPricePerStar) *
            100

        return {
            ...option,
            calculatedDiscount: Math.ceil(Math.max(0, savings)),
        }
    })
})

// Watch for modelValue changes to prevent opening when payment is in progress
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue && paymentStore.isPaymentInProgress()) {
            // If a payment is in progress, don't open the modal
            console.log(
                'Payment in progress, preventing buy stars modal from opening'
            )
            emit('update:modelValue', false)
        }
    }
)

const handleModalUpdate = (newValue) => {
    // If a payment is in progress and trying to open the modal, prevent it
    if (newValue && paymentStore.isPaymentInProgress()) {
        console.log(
            'Payment in progress, preventing buy stars modal from opening'
        )
        emit('update:modelValue', false)
        return
    }

    emit('update:modelValue', newValue)
}

const selectOption = (option) => {
    selectedOption.value = option
    if (error.value) {
        packStore.error = null
    }
}

const handleCheckout = async () => {
    if (!selectedOption.value) return

    // Check if user is anonymous
    if (authStore.isAnonymous) {
        // Close the buy stars modal
        emit('update:modelValue', false)
        // Store the pending action and pack ID for after login/upgrade
        try {
            localStorage.setItem('pendingAction', 'buyStars')
            localStorage.setItem('pendingPackId', selectedOption.value.id)
        } catch (e) {
            console.error('Failed to store pending action:', e)
        }
        // Open the login modal which will show Continue2 component
        modalStore.openModal({ darkMode: true })
        return
    }

    // Check if user is authenticated
    if (!authStore.userId) {
        // Close the buy stars modal
        emit('update:modelValue', false)
        // Open the login modal
        modalStore.openModal({ darkMode: true })
        return
    }

    processingPayment.value = true
    try {
        // Close the modal before redirecting
        emit('update:modelValue', false)

        // Get the optional tracker from the query parameters and clear it
        const optionalTracker = localStorage.getItem('tfTracker')
        if (optionalTracker) {
            localStorage.removeItem('tfTracker')
        }

        // Get AdCash click ID from localStorage (captured on landing page)
        const adcashClickId = localStorage.getItem('adcashClickId')

        // Create the payment session and redirect to checkout
        await transactionService.createPayin(
            selectedOption.value.id,
            optionalTracker,
            undefined, // paymentProcessor
            undefined, // paymentMethod
            undefined, // amount
            adcashClickId // adcashClickId
        )
    } catch (error) {
        console.error('Error initiating checkout:', error)
    } finally {
        processingPayment.value = false
    }
}

// Watch for purchase options to become available and pre-select one
watch(
    purchaseOptions,
    (newOptions) => {
        if (newOptions.length > 0 && !selectedOption.value) {
            const preselectOption =
                newOptions.find((opt) => opt.amount >= 500) ||
                newOptions[Math.floor(newOptions.length / 2)]
            if (preselectOption) {
                selectOption(preselectOption)
            }
        }
    },
    { immediate: true }
)

onMounted(() => {
    // Fetch packs immediately when modal is mounted
    if (!purchaseOptions.value || purchaseOptions.value.length === 0) {
        packStore.fetchPacks()
    }

    // Also watch for modal opening
    watch(
        () => props.modelValue,
        (isOpen) => {
            if (
                isOpen &&
                (!purchaseOptions.value || purchaseOptions.value.length === 0)
            ) {
                packStore.fetchPacks()
            }
        },
        { immediate: true }
    )
})
</script>

<style scoped>
/* Override modal container max-width for this specific modal */
:deep(.modal-container) {
    max-width: 500px;
}

/* Main content container */
.purchase-stars-modal {
    color: #ffffff;
    font-family: 'Nunito', sans-serif;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 80px 36px 30px;
    position: relative;
}

.modal-header {
    text-align: center;
    margin-bottom: 24px;
    width: 100%;
}

.title {
    font-size: 32px;
    font-weight: 400;
    font-family: 'Basier Circle', sans-serif;
    line-height: 38px;
    max-width: 260px;
    text-align: center;
    margin: auto;
    margin-bottom: 32px;
    color: #ffffff;
}

.subtitle-text {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.75);
    line-height: 19px;
    max-width: 360px;
    margin: 0 auto;
}

.purchase-options-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin-bottom: 24px;
}

.option-left {
    display: flex;
    align-items: center;
    gap: 4px;
}

.stars-container {
    position: relative;
    display: inline-block;
    width: 22px;
    height: 18px;
}

.star-icon {
    color: #ffc641;
    font-size: 17px;
    position: absolute;
    top: 0;
    left: 0;
    -webkit-text-stroke: 1px #0c0d23;
    text-stroke: 1px #0c0d23;
}

.star-second {
    left: 4px;
}

.stars-amount {
    font-size: 14px;
    line-height: 1;
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    color: #ffffff;
    display: contents;
}

.option-right {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    min-width: 130px;
}

.price {
    font-size: 14px;
    font-family: 'Nunito', sans-serif;
    font-weight: 700;
    color: #ffffff;
    display: contents;
}

.discount-badge {
    background: rgba(154, 85, 242, 0.62);
    color: #ffffff;
    font-size: 11px;
    line-height: 1;
    font-family: 'Nunito', sans-serif;
    font-weight: 600;
    padding: 4px 7px;
    border-radius: 8px;
    white-space: nowrap;
}

.continue-button {
    width: 100%;
    max-width: 400px;
    height: 50px;
    background: linear-gradient(135deg, #6e3dea 0%, #9a55f2 100%);
    border: none;
    border-radius: 24px;
    font-size: 15px;
    font-weight: 600;
    font-family: 'Basier Circle', sans-serif;
}

.continue-button:hover:not(:disabled) {
    opacity: 0.9;
}

.continue-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.loading-message,
.error-message {
    text-align: center;
    padding: 40px 20px;
    font-size: 14px;
    color: #a0a3bd;
    width: 100%;
}

.error-message {
    color: #ff6b6b;
}
</style>
